/**
 * The practice result (screens §P2 step 3, chair DESIGN §4): what the player's practice settings would do to the bank and its share price
 * against the twin, as one results page: the verdict in dollars a share, four measures, the quarter and year table, the causes and the
 * price arithmetic, then the close-out card. Practice only: never scored, never saved, never in P9.
 */
import type { ReactNode } from "react";
import type { ContentCatalog, PracticeResult as PracticeResultDto } from "@bsim/contracts";
import { KPI_DEFS, type KpiId, type KpiSnapshot } from "@bsim/domain";
import { formatMetric } from "@gxs/metric-defs";
import { causeLabel } from "../cockpit/Why";
import { formatBp, formatKpiChange, formatKpi, formatMultiple, formatPoints, formatUsdM, formatUsdPerShare } from "../format";
import { LOWER_IS_BETTER } from "../format/labels";
import { plainLabel } from "./plainLabels";
import { GxCard } from "../theme/primitives";
import { GxTermText } from "../theme/GxTerm";
import { RevealHeader } from "./RevealHeader";
import "./practice.css";

/** A margin gap under ten basis points is read to a tenth ("+0.8bp"), whole basis points above: the tile, the table and the sentence print one figure. */
const FINE_BELOW_BP = 10;
const roundedBp = (bp: number, fine: boolean): number => (fine ? Math.round(bp * 10) / 10 : Math.round(bp));
export const gapBpText = (bp: number): string => (Number.isInteger(bp) ? formatBp(bp, { signed: true }) : formatMetric(bp, "bp", { signed: true, precision: 1 }));
const bpGapText = (delta: number): string => gapBpText(roundedBp(delta * 10_000, Math.abs(delta) * 10_000 < FINE_BELOW_BP));
/** Half a cent: a share-price gap under it prints as $0.00, so it is level. */
const LEVEL_PRICE = 0.005;
const NO_VALUE = "n/a";
const isZeroText = (text: string): boolean => !/[1-9]/.test(text);

type Tone = "good" | "crit" | "neutral";
interface Gap { readonly text: string; readonly tone: Tone }

/** The gap to the twin (you minus your twin) as text and tone. Colour follows who it helps: a lower-is-better measure turns the other way; a level gap is neutral and says "level". */
export function gapOf(kpi: KpiId, delta: number | undefined, short = false): Gap | null {
  if (delta === undefined) return null;
  const unit = KPI_DEFS[kpi].unit;
  let text: string;
  if (kpi === "price") text = formatUsdPerShare(delta, { signed: true });
  else if (unit === "rate" && formatKpiChange(kpi, delta).endsWith("bp")) text = bpGapText(delta);
  else if (unit === "rate" && / points$/.test(formatKpiChange(kpi, delta))) text = formatPoints(delta * 100, { dp: 2, signed: true, short });
  else text = formatKpiChange(kpi, delta, { signed: true });
  if (short) text = text.replace(/ points$/, " pts");
  if (isZeroText(text)) return { text: "level", tone: "neutral" };
  const helps = LOWER_IS_BETTER.has(kpi) ? delta < 0 : delta > 0;
  return { text, tone: helps ? "good" : "crit" };
}

/** Deposits won (+) or lost (−) against the twin: the one wording everywhere. */
export function depositsLine(usd: number): string {
  const text = formatUsdM(Math.abs(usd), { signed: false });
  return isZeroText(text) ? "Deposits level with your twin" : usd > 0 ? `Deposits won against your twin: +${text}` : `Deposits lost against your twin: −${text}`;
}

interface Horizon { readonly months: 3 | 12; readonly kpis: KpiSnapshot; readonly deltas: KpiSnapshot }

const twinValue = (h: Horizon, kpi: KpiId): number | undefined => {
  const [v, d] = [h.kpis[kpi], h.deltas[kpi]];
  return v === undefined || d === undefined ? undefined : v - d;
};

export interface PriceCause { readonly label: string; readonly usd: number }

/**
 * The quarter's share-price gap by cause, from the attribution's own price deltas: the settings the player moved (`moved`, by label; even at $0.00, so they see the setting did nothing), then any other cause that moved the price,
 * largest first, then "Everything else" so the rows sum to the gap. Null while the attribution carries no price.
 */
export function priceCauses(result: PracticeResultDto, catalog: ContentCatalog, moved: readonly string[] = []): { readonly rows: readonly PriceCause[]; readonly rest: number } | null {
  const gap = result.deltasVsTwin.price;
  if (gap === undefined) return null;
  const cents = (usd: number): number => Math.round(usd * 100) / 100;
  const rows = result.attribution.byCause
    .filter((c) => c.cause.kind !== "contractual" && c.cause.kind !== "autopilot")
    .map((c) => ({ label: causeLabel(c.cause, catalog), usd: cents(c.kpiDeltas["price"] ?? 0), keep: c.cause.kind === "decision" && moved.includes(causeLabel(c.cause, catalog)) }))
    .filter((r) => r.keep || r.usd !== 0)
    .sort((a, b) => Math.abs(b.usd) - Math.abs(a.usd))
    .map(({ label, usd }) => ({ label, usd }));
  return { rows, rest: cents(cents(gap) - rows.reduce((sum, r) => sum + r.usd, 0)) };
}

const signedUsd = (usd: number): string => (usd === 0 ? formatUsdPerShare(0) : formatUsdPerShare(usd, { signed: true }));
const toneClass = (tone: Tone): string => (tone === "good" ? "gx-pr-pos" : tone === "crit" ? "gx-pr-neg" : "");

/** Cause rows on navy: the label and its dollars a share, colour for who it helps (the on-navy tones). */
function DriverRows({ rows, rest }: { readonly rows: readonly PriceCause[]; readonly rest: number }) {
  const tone = (usd: number): string => (usd > 0 ? "gx-pr-pos" : usd < 0 ? "gx-pr-neg" : "");
  return (
    <ul className="gx-pr-drivers gx-num" data-testid="practice.moved">
      {rows.map((r) => <li key={r.label}><span>{r.label}</span><b className={tone(r.usd)}>{signedUsd(r.usd)}</b></li>)}
      <li><span>Everything else</span><b>{formatUsdPerShare(rest)}</b></li>
    </ul>
  );
}

/** The verdict sentence under the headline: both prices, the gap in points of shareholder value, and what it is made of. */
function verdictSentence(h: Horizon, deltasDps: number | undefined): ReactNode {
  const [price, gap] = [h.kpis.price, h.deltas.price];
  if (price === undefined || gap === undefined) return null;
  const twin = price - gap;
  const points = twin === 0 ? 0 : (Math.abs(gap) / twin) * 100;
  const pointsText = formatMetric(points, "index", { precision: 2 });
  const cash = deltasDps === undefined || Math.abs(deltasDps) < LEVEL_PRICE ? null : deltasDps;
  const made = cash === null ? `all of it share price: you and your twin pay out the same` : `of which ${formatUsdPerShare(Math.abs(cash))} is cash returned`;
  return <>Your share price ends at <b>{formatKpi("price", price)}</b>; your twin&apos;s ends at <b>{formatKpi("price", twin)}</b>. The gap is <b>{pointsText} percentage points</b> of your twin&apos;s value per share, {made}.</>;
}

/** The hero's left block. The headline is the dollar gap to the twin on the longest horizon the engine ran. */
function Verdict({ h, year, dps }: { readonly h: Horizon; readonly year: boolean; readonly dps: number | undefined }) {
  const gap = h.deltas.price;
  const price = h.kpis.price;
  const month = h.months;
  const level = gap === undefined || Math.abs(gap) < LEVEL_PRICE;
  const eyebrow = year ? "Your settings kept for a year, against your twin" : "Your settings after a quarter, against your twin";
  const twin = price !== undefined && gap !== undefined ? price - gap : undefined;
  return (
    <>
      <span className="gx-pr-badge" data-testid="practice.badge">Practice. Not scored, not saved.</span>
      <div className="gx-eyebrow">{eyebrow}</div>
      <div className="gx-pr-big" data-testid="practice.lead">
        {gap === undefined ? <span className="gx-pr-quiet">No share price to compare</span>
          : level ? <span className="gx-pr-words">{year ? "Level with your twin" : "Level with your twin after a quarter"}</span>
            : <><span className="gx-pr-fig">{formatUsdPerShare(Math.abs(gap))} a share</span> <span className="gx-pr-words">{gap < 0 ? "behind" : "ahead of"} your twin{year ? "" : " after a quarter"}</span></>}
      </div>
      {gap === undefined ? null : level && !year
        ? <p className="gx-hero-sub" data-testid="practice.slow">Hiring changes take a year to show; the first quarter lands on your twin&apos;s path.</p>
        : <p className="gx-hero-sub" data-testid="practice.sentence">{verdictSentence(h, dps)}</p>}
      {price === undefined || twin === undefined ? null : (
        <div className="gx-pr-pair gx-num">
          <div><b>{formatKpi("price", price)}</b><span>Your share price, month {month}</span></div>
          <div><b>{formatKpi("price", twin)}</b><span>Your twin&apos;s share price, month {month}</span></div>
        </div>
      )}
    </>
  );
}

function Drivers({ result, catalog, moved, onAskCoach }: { readonly result: PracticeResultDto; readonly catalog: ContentCatalog; readonly moved: readonly string[]; readonly onAskCoach: () => void }) {
  const [price, gap] = [result.kpis.price, result.deltasVsTwin.price];
  const causes = priceCauses(result, catalog, moved);
  const shown = causes !== null && (causes.rows.some((r) => r.usd !== 0) || causes.rest !== 0);
  return (
    <>
      <div className="gx-eyebrow">What drove the first quarter</div>
      {price === undefined || gap === undefined ? null
        : <p className="gx-pr-cap" data-testid="practice.explains">Share price after three months: you {formatKpi("price", price)}, your twin {formatKpi("price", price - gap)}, a gap of <span className="gx-nowrap">{signedUsd(gap)}</span>.{shown ? " By cause:" : ""}</p>}
      {causes === null ? null : shown ? <DriverRows rows={causes.rows} rest={causes.rest} /> : <p className="gx-pr-cap">Nothing moved the price against your twin this quarter.</p>}
      <button type="button" className="gx-btn gx-btn--onnavy gx-pr-ask" onClick={onAskCoach}>Ask the Coach why</button>
    </>
  );
}

/** A measure tile: eyebrow, figure, then "Your twin X" with the gap tag in the same card. */
function Tile({ eyebrow, figure, figureTone = "neutral", twinText, tag, testId }: {
  readonly eyebrow: string; readonly figure: string; readonly figureTone?: Tone; readonly twinText: string; readonly tag: Gap | null; readonly testId: string;
}) {
  return (
    <GxCard className="gx-pr-kpi" testId={testId}>
      <div className="gx-card-b gx-num">
        <div className="gx-eyebrow">{eyebrow}</div>
        <div className={`gx-pr-v ${figureTone === "good" ? "gx-pr-good" : figureTone === "crit" ? "gx-pr-bad" : ""}`}>{figure}</div>
        <div className="gx-pr-row"><span>{twinText}</span>{tag === null ? null : <span className={`gx-pr-tag gx-pr-tag--${tag.tone}`}>{tag.text}</span>}</div>
      </div>
    </GxCard>
  );
}

const orDash = (kpi: KpiId, v: number | undefined): string => (v === undefined ? NO_VALUE : formatKpi(kpi, v));

function Strip({ h, depositsUsd }: { readonly h: Horizon; readonly depositsUsd: number | undefined }) {
  const after = h.months === 12 ? "" : ` after ${h.months} months`;
  const month = `month ${h.months}`;
  const tile = (kpi: KpiId, eyebrow: string, key: string) => {
    const twin = twinValue(h, kpi);
    return <Tile key={key} testId={`practice.tile.${key}`} eyebrow={eyebrow} figure={orDash(kpi, h.kpis[kpi])} twinText={`Your twin ${orDash(kpi, twin)}`} tag={gapOf(kpi, h.deltas[kpi], true)} />;
  };
  // The year has the deposits won against the twin (the contract's one deposit figure); the quarter has the growth rates, so it reads those.
  const deposits = depositsUsd !== undefined
    ? (() => {
        const tone: Tone = depositsUsd === 0 ? "neutral" : depositsUsd > 0 ? "good" : "crit";
        const word = depositsUsd === 0 ? "level" : depositsUsd > 0 ? "ahead" : "behind";
        const sign = formatUsdM(Math.abs(depositsUsd), { signed: false });
        return <Tile key="deposits" testId="practice.tile.deposits" eyebrow={depositsUsd < 0 ? "Deposits lost against your twin" : "Deposits won against your twin"} figure={isZeroText(sign) ? "$0.0M" : `${depositsUsd > 0 ? "+" : "−"}${sign}`} figureTone={tone}
          twinText={`The gap in deposits after ${h.months} months`} tag={{ text: word, tone }} />;
      })()
    : tile("depositGrowth", `Deposit growth${after}`, "deposits");
  return (
    <div className="gx-pr-kpis" data-testid="practice.kpis">
      {tile("rotce", `Return on tangible equity${after}`, "rotce")}
      {tile("nim", `Net interest margin${after}`, "nim")}
      {tile("nii", `Net interest income, ${month}`, "nii")}
      {deposits}
    </div>
  );
}

const TABLE_KPIS: readonly KpiId[] = ["price", "rotce", "nim", "efficiencyRatio", "cet1Reported", "cet1Marked", "ncoRate"];
/** The row's plain name from the shared Results helper, the code only in brackets after it; the efficiency ratio says which way is better. */
const TABLE_ROWS: readonly { readonly kpi: KpiId; readonly label: string }[] = TABLE_KPIS.map((kpi) => ({ kpi, label: kpi === "efficiencyRatio" ? `${plainLabel(kpi)} (lower is better)` : plainLabel(kpi) }));

function HorizonCells({ h, kpi }: { readonly h: Horizon; readonly kpi: KpiId }) {
  const v = h.kpis[kpi];
  const gap = gapOf(kpi, h.deltas[kpi], true);
  return (
    <>
      <td className={v === undefined ? "gx-faint" : ""}>{orDash(kpi, v)}</td>
      <td className={`gx-pr-gap ${gap === null ? "gx-faint" : toneClass(gap.tone)}`}>{gap === null ? NO_VALUE : gap.text}</td>
    </>
  );
}

function QuarterAndYear({ quarter, year }: { readonly quarter: Horizon; readonly year: Horizon | null }) {
  const groups = year === null ? [quarter] : [quarter, year];
  return (
    <GxCard title="Quarter and year, against your twin" aside={<span className="gx-small gx-faint">Gap = you minus your twin</span>} className="gx-pr-table" testId="practice.year">
      <div className="gx-card-b">
        <div className="gx-pr-scroll" role="region" tabIndex={0} aria-label="Quarter and year against your twin, scrolls sideways">
        <table className="gx-table gx-num">
          <thead>
            <tr className="gx-pr-grp"><th aria-hidden="true" />{groups.map((g) => <th key={g.months} colSpan={2}><span>After {g.months} months</span></th>)}</tr>
            <tr><th scope="col">Measure</th>{groups.flatMap((g) => [<th key={`${g.months}y`} scope="col">You</th>, <th key={`${g.months}g`} scope="col">Gap</th>])}</tr>
          </thead>
          <tbody>
            {TABLE_ROWS.map((r) => (
              <tr key={r.kpi} data-testid={`practice.year.${r.kpi}`}><th scope="row">{r.label}</th>{groups.map((g) => <HorizonCells key={g.months} h={g} kpi={r.kpi} />)}</tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      <div className="gx-card-f" data-testid="practice.basis">
        {year === null ? "The quarter runs your settings with no events, against your twin on the standing policy. " : "The twelve months run your settings with no events, against your twin's twelve months on the standing policy. "}
        bp means basis points: 100bp = 1 percentage point.
      </div>
    </GxCard>
  );
}

function PriceSource({ quarter }: { readonly quarter: Horizon }) {
  const line = (who: string, [ptbv, tbvps, price]: readonly [number | undefined, number | undefined, number | undefined]) => (
    // One grid, three rows (who, figure, caption): the two signs sit in the figure row, so they share the figures' baseline and size.
    <div className="gx-pr-eq">
      <span>{who}</span><span aria-hidden="true" /><span aria-hidden="true" /><span aria-hidden="true" /><span aria-hidden="true" />
      <b>{ptbv === undefined ? NO_VALUE : formatMultiple(ptbv)}</b><i aria-hidden="true">&times;</i><b>{tbvps === undefined ? NO_VALUE : formatKpi("tbvps", tbvps)}</b><i aria-hidden="true">=</i><b>{price === undefined ? NO_VALUE : formatKpi("price", price)}</b>
      <span>price to book</span><span aria-hidden="true" /><span>book a share</span><span aria-hidden="true" /><span>share price</span>
    </div>
  );
  const mine = [quarter.kpis.ptbv, quarter.kpis.tbvps, quarter.kpis.price] as const;
  const theirs = [twinValue(quarter, "ptbv"), twinValue(quarter, "tbvps"), twinValue(quarter, "price")] as const;
  return (
    <GxCard title="Where the price comes from" testId="practice.price">
      <div className="gx-card-b gx-num">
        <p className="gx-small gx-faint">After three months. Price = what the market pays for each dollar of tangible book &times; tangible book per share.</p>
        {line("Your bank", mine)}
        {line("Your twin", theirs)}
      </div>
    </GxCard>
  );
}

export function PracticeResultView({ result, catalog, twinLine, moved = [], onAskCoach, onChange, onReady }: {
  /** Labels of the settings the player moved off the standing policy: each is listed among the causes even when it moved nothing. */
  readonly moved?: readonly string[];
  readonly result: PracticeResultDto; readonly catalog: ContentCatalog; readonly twinLine: string;
  readonly onAskCoach: () => void;
  /** "Change a setting": back to the practice settings. */
  readonly onChange: () => void;
  /** "Ready for Round 1": on to the page that opens round 1. */
  readonly onReady: () => void;
}) {
  const quarter: Horizon = { months: 3, kpis: result.kpis, deltas: result.deltasVsTwin };
  const year: Horizon | null = result.year === undefined ? null : { months: 12, kpis: result.year.kpis, deltas: result.year.deltasVsTwin };
  const lead = year ?? quarter;
  const dps = lead.deltas["dps"];
  return (
    <div className="gx-pr" data-testid="chair.practice.result" data-practice={result.practice}>
      <RevealHeader testId="practice.hero" lead={<Verdict h={lead} year={year !== null} dps={dps} />} aside={<Drivers result={result} catalog={catalog} moved={moved} onAskCoach={onAskCoach} />} />
      <Strip h={lead} depositsUsd={year === null ? undefined : result.year?.depositsDeltaUsd ?? 0} />
      <div className="gx-pr-cols">
        <QuarterAndYear quarter={quarter} year={year} />
        <div className="gx-pr-side">
          <PriceSource quarter={quarter} />
          <GxCard title="Your twin" testId="practice.twin"><div className="gx-card-b"><GxTermText>{twinLine}</GxTermText></div></GxCard>
        </div>
      </div>
      <section className="gx-card gx-pr-close" data-testid="practice.close" aria-label="End of practice">
        <div><h2 className="gx-h2">Practice over</h2><p data-testid="practice.note">Your real game starts on the standing policy. Nothing you tried here carries into it.</p></div>
        <div className="gx-pr-acts"><button type="button" className="gx-btn gx-btn--secondary" data-testid="chair.practice.back" onClick={onChange}>Change a setting</button><button type="button" className="gx-btn gx-btn--primary" data-testid="chair.ready" onClick={onReady}>Ready for Round 1</button></div>
      </section>
    </div>
  );
}

